<template>
  <div class="did-number">
    <v-card v-if="did" class="mb-4">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-tooltip v-if="did.countryCode" :text="getCountryNameFromCode(did.countryCode)" location="top">
            <template #activator="{ props }">
              <span v-bind="props" class="mr-2" style="font-size: 1.5rem;">{{ getFlagEmoji(did.countryCode) }}</span>
            </template>
          </v-tooltip>
          <h2>{{ did.number }}</h2>
        </div>
        <v-btn
          color="primary"
          :prepend-icon="mdiPencil"
          @click="openEditDialog"
        >
          Edit DID
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="[did]"
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:item.description="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiInformation" class="mr-2" />
              {{ item.description || 'No description' }}
            </div>
          </template>

          <template v-slot:item.dateAdded="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiCalendar" class="mr-2" />
              {{ formatDate(item.dateAdded) }}
            </div>
          </template>

          <template v-slot:item.monthlyRate="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiCurrencyUsd" class="mr-2" />
              {{ formatCurrency(item.monthlyRate) }}
            </div>
          </template>

          <template v-slot:item.nextBillingDate="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiCalendarClock" class="mr-2" />
              {{ formatDate(item.nextBillingDate) }}
            </div>
          </template>

          <template v-slot:item.isSmsEnabled="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiMessageText" class="mr-2" />
              {{ item.isSmsEnabled ? 'Yes' : 'No' }}
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <DidRoutes
      v-if="did"
      :did-id="did.id"
      @update="fetchDid"
    />

    <!-- Edit DID Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>Edit DID</v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="form.description"
              label="Description"
              :rules="[(v: string) => !v || v.length <= 255 || 'Description too long']"
            />

            <v-switch
              v-model="form.isSmsEnabled"
              label="Enable SMS"
              color="primary"
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            @click="dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!valid"
            @click="saveDid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useNotificationStore } from '@/stores/notification'
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper'
import { getFlagEmoji, getCountryNameFromCode } from '@/utils/countryUtils'
import DidRoutes from './DidRoutes.vue'
import { mdiPencil, mdiInformation, mdiCalendar, mdiCurrencyUsd, mdiCalendarClock, mdiMessageText } from '@mdi/js'

interface DidNumber {
  id: number
  number: string
  description: string
  custMaxChannels: number
  isSmsEnabled: boolean
  monthlyRate: number
  nextBillingDate: string
  dateAdded: string
  activationFee: number
  commitmentTimeTillCustomer: string | null
  ratePerMin: number
  countryCode: string
}

const route = useRoute()
const did = ref<DidNumber | null>(null)
const loading = ref(false)
const dialog = ref(false)
const valid = ref(false)
const form = ref({
  description: '',
  isSmsEnabled: false
})

const headers = [
  { title: 'Description', key: 'description', sortable: false },
  { title: 'Date Added', key: 'dateAdded', sortable: false },
  { title: 'Monthly Rate', key: 'monthlyRate', sortable: false },
  { title: 'Next Billing Date', key: 'nextBillingDate', sortable: false },
  { title: 'SMS Enabled', key: 'isSmsEnabled', sortable: false }
]

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount)
}

const fetchDid = async () => {
  loading.value = true
  try {
    const response = await fetchWrapper.get(`/numbers/${route.params.id}/`)
    did.value = response.data as DidNumber
    form.value = {
      description: did.value.description || '',
      isSmsEnabled: did.value.isSmsEnabled
    }
  } catch (error) {
    useNotificationStore().showError({
      title: 'Error',
      text: 'Failed to fetch DID number'
    })
  } finally {
    loading.value = false
  }
}

const openEditDialog = () => {
  if (did.value) {
    form.value = {
      description: did.value.description || '',
      isSmsEnabled: did.value.isSmsEnabled
    }
    dialog.value = true
  }
}

const saveDid = async () => {
  if (!did.value) return

  loading.value = true
  try {
    await fetchWrapper.put(`/numbers/${did.value.id}/`, form.value)
    dialog.value = false
    await fetchDid()
    useNotificationStore().showSuccess({
      title: 'Success',
      text: 'DID number updated successfully'
    })
  } catch (error) {
    useNotificationStore().showError({
      title: 'Error',
      text: 'Failed to update DID number'
    })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDid()
})
</script>

<style scoped>
.did-number {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}
</style> 